import React from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Step,
  StepIndicator,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/joy';
import type { ColorPaletteProp } from '@mui/joy';
import { Check } from '@phosphor-icons/react';
import {
  FORWARD_LIFECYCLE_STAGES,
  TERMINAL_LIFECYCLE_STAGES,
} from '../../types/admin';
import type { LifecycleStage } from '../../types/admin';
import { useIsMobile } from '../../hooks/useIsMobile';

export const LIFECYCLE_META: Record<
  LifecycleStage,
  { label: string; color: ColorPaletteProp; description: string }
> = {
  lead: {
    label: 'Lead',
    color: 'neutral',
    description: 'Filled in the website form, no trial account yet.',
  },
  qualifying: {
    label: 'Qualifying',
    color: 'primary',
    description: 'A larger lead we are working before they start a trial.',
  },
  trialing: {
    label: 'Trialing',
    color: 'primary',
    description: 'On a free trial.',
  },
  activated: {
    label: 'Activated',
    color: 'success',
    description: 'Paying and current.',
  },
  // Lost is neutral, not warning, and Churned is warning, not danger — these
  // chips sit beside the subscription chip from `statusChipColor`, which maps
  // canceled to warning and past_due to danger. Making Churned danger would put
  // a danger lifecycle chip next to a warning subscription chip on the same
  // row, and a past-due-but-still-Activated customer would show danger beside
  // success. If danger is wanted for Churned, change `statusChipColor` in the
  // same commit so the two never disagree.
  lost: {
    label: 'Lost',
    color: 'neutral',
    description: 'Never activated.',
  },
  churned: {
    label: 'Churned',
    color: 'warning',
    description: 'Was a paying customer, then cancelled or stopped paying.',
  },
};

interface LifecycleStageBarProps {
  stage: LifecycleStage;
  /**
   * The furthest forward stage this contact ever reached. For a churned
   * customer that is `activated`, which is what freezes the bar in the right
   * place. Defaults to `stage` when the contact has not exited.
   */
  furthestStage?: LifecycleStage;
  /** Why the stage is what it is, e.g. `free_plan_never_billed`. */
  reason?: string;
  onStageClick?: (stage: LifecycleStage) => void;
}

const isTerminal = (stage: LifecycleStage) => TERMINAL_LIFECYCLE_STAGES.includes(stage);

/**
 * The lifecycle of one contact, across the top of their record.
 *
 * Only the four forward stages are `Step`s. Lost and Churned are terminal
 * exits shown as a chip after a dashed connector: rendering them as steps five
 * and six would assert that Activated → Churned is forward progress, which is
 * the one thing a progress bar must not say. When a contact has exited, the
 * four steps freeze at the furthest stage they reached and fade back, so the
 * history stays readable without competing with the exit.
 *
 * Follows `components/setup/SetupProgressIndicator.tsx` for the stepper
 * treatment so the two read as the same control.
 */
export const LifecycleStageBar: React.FC<LifecycleStageBarProps> = ({
  stage,
  furthestStage,
  reason,
  onStageClick,
}) => {
  const isMobile = useIsMobile();
  const exited = isTerminal(stage);
  const meta = LIFECYCLE_META[stage];

  // Where the forward path stopped. An exited contact freezes at the furthest
  // stage they reached; everyone else is simply at their current stage.
  const pathStage: LifecycleStage = exited
    ? furthestStage && !isTerminal(furthestStage)
      ? furthestStage
      : 'lead'
    : stage;
  const pathIndex = FORWARD_LIFECYCLE_STAGES.indexOf(pathStage);

  // A four-label stepper is unreadable at 375px whether or not it scrolls, so
  // below md the same information becomes a determinate bar plus the stage name
  // (the treatment `UsageLimitIndicator` already uses).
  if (isMobile) {
    const reached = pathIndex + 1;
    return (
      <Box sx={{ width: '100%', display: 'grid', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Chip size="sm" variant="soft" color={meta.color}>
            {meta.label}
          </Chip>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {exited ? `Reached ${LIFECYCLE_META[pathStage].label}` : `Step ${reached} of 4`}
          </Typography>
        </Box>
        <LinearProgress
          determinate
          value={(reached / FORWARD_LIFECYCLE_STAGES.length) * 100}
          color={exited ? 'neutral' : meta.color}
          sx={{ height: 6 }}
        />
        {reason && (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {reason.replace(/_/g, ' ')}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          pb: 1,
          opacity: exited ? 0.6 : 1,
        }}
      >
        <Stepper
          size="sm"
          sx={{
            width: '100%',
            minWidth: 'fit-content',
            '--StepIndicator-size': '28px',
            '--Step-gap': '8px',
            '--Step-connectorInset': '0px',
            '--Step-connectorRadius': '4px',
            '--Step-connectorThickness': '2px',
          }}
        >
          {FORWARD_LIFECYCLE_STAGES.map((forwardStage, index) => {
            const isCompleted = index < pathIndex || (exited && index <= pathIndex);
            const isCurrent = !exited && index === pathIndex;
            const isClickable = !!onStageClick;
            const stageMeta = LIFECYCLE_META[forwardStage];

            return (
              <Step
                key={forwardStage}
                completed={isCompleted}
                active={isCurrent}
                indicator={
                  <Tooltip title={stageMeta.description} size="sm" variant="soft">
                    <StepIndicator
                      variant={isCompleted ? 'solid' : isCurrent ? 'soft' : 'outlined'}
                      color={isCompleted ? 'success' : isCurrent ? 'primary' : 'neutral'}
                      sx={{
                        cursor: isClickable ? 'pointer' : 'default',
                        '&:hover': isClickable ? { boxShadow: 'sm' } : {},
                      }}
                      onClick={() => onStageClick?.(forwardStage)}
                    >
                      {isCompleted ? <Check size={16} /> : index + 1}
                    </StepIndicator>
                  </Tooltip>
                }
                sx={{
                  '&::after': {
                    bgcolor: isCompleted ? 'success.500' : 'neutral.300',
                  },
                }}
              >
                <Typography
                  level="body-xs"
                  sx={{
                    color: isCurrent
                      ? 'primary.600'
                      : isCompleted
                        ? 'success.600'
                        : 'neutral.500',
                    fontWeight: isCurrent ? 600 : 400,
                    whiteSpace: 'nowrap',
                    cursor: isClickable ? 'pointer' : 'default',
                  }}
                  onClick={() => onStageClick?.(forwardStage)}
                >
                  {stageMeta.label}
                </Typography>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      {exited && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pb: 1 }}>
          {/* Dashed, so the exit reads as a departure from the path rather
              than another step along it. */}
          <Box
            aria-hidden
            sx={{
              width: 28,
              borderTop: '2px dashed',
              borderColor: 'neutral.300',
            }}
          />
          <Tooltip title={reason ? `${meta.description} (${reason.replace(/_/g, ' ')})` : meta.description} size="sm" variant="soft">
            <Chip size="sm" variant="soft" color={meta.color}>
              {meta.label}
            </Chip>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default LifecycleStageBar;
